import _classNames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { formatNumber } from '../../common/js/utilities';
import Button from '../button/button';

/**
 * A functional component for adding pagination controls to a collection of data. NOTE: You must have either a `totalPages` OR an `itemTotal` prop in order for this component to render.
 */
function Pagination({
  classes,
  isFuzzy,
  itemLabel,
  itemTotal,
  minimal,
  onFastForwardClick,
  onNextClick,
  onPreviousClick,
  onRewindClick,
  pageCurrent,
  pageLimit,
  pageSize,
  totalPages,
}) {
  const displayRangeStart = pageCurrent * pageSize - pageSize + 1;
  let displayRangeEnd = pageCurrent * pageSize;
  if (displayRangeEnd > itemTotal) {
    displayRangeEnd = itemTotal;
  }

  const pageTotal = totalPages || Math.ceil(itemTotal / pageSize);
  const previousDisabled = pageCurrent === 1;
  const nextDisabled = pageCurrent === pageTotal || (pageLimit && pageCurrent > pageLimit);
  const hasRewindFastForward = !!onFastForwardClick && !!onRewindClick;

  return pageCurrent && pageTotal ? (
    <div
      className={_classNames('Pagination', classes, {
        'Pagination--minimal': minimal,
      })}
    >
      {!minimal && (
        <div className="Pagination__page-indicator">
          {`${itemLabel} ${formatNumber(displayRangeStart)}`}
          {pageLimit && pageCurrent > pageLimit
            ? '+'
            : `-${formatNumber(displayRangeEnd)} of ${formatNumber(itemTotal)}`}
        </div>
      )}
      <div className="Pagination__page-control">
        {pageTotal > 1 && (
          <div className="Pagination__page-control__buttons">
            {hasRewindFastForward && (
              <Button
                disabled={previousDisabled}
                iconLeft="Chevron--double--west"
                onClick={onRewindClick}
                small
                type="secondary"
              />
            )}
            <Button
              disabled={previousDisabled}
              iconLeft="Chevron--single--west"
              onClick={onPreviousClick}
              small
              type="secondary"
            >
              {isFuzzy ? <>Previous {pageSize}</> : null}
            </Button>
            <div className="Pagination__page-control__separator" />
            <Button
              disabled={nextDisabled}
              iconRight="Chevron--single--east"
              onClick={onNextClick}
              small
              type="secondary"
            >
              {isFuzzy ? <>Next {pageSize}</> : null}
            </Button>
            {!isFuzzy && hasRewindFastForward && (
              <Button
                disabled={nextDisabled}
                iconLeft="Chevron--double--east"
                onClick={onFastForwardClick}
                small
                type="secondary"
              />
            )}
          </div>
        )}
      </div>
    </div>
  ) : null;
}

Pagination.defaultProps = {
  isFuzzy: false,
  minimal: false,
  classes: '',
  itemLabel: '',
  itemTotal: undefined,
  onFastForwardClick: undefined,
  onRewindClick: undefined,
  pageLimit: undefined,
  totalPages: undefined,
};

Pagination.propTypes = {
  /**
   * A space-separated list of classes to add to the outer `div` wrapping the component
   */
  classes: PropTypes.string,
  /**
   * Set to `true` when `itemTotal` is indeterminate or to change pagination button appearance
   * to include only rewind, previous, and next (no fast forward)
   */
  isFuzzy: PropTypes.bool,
  /**
   * The name of your table items in the callout text
   */
  itemLabel: PropTypes.string,
  /**
   * Total number of items (if you are not using this prop, you must include a `totalPages` value)
   */
  itemTotal: PropTypes.number,
  /**
   * Set to `true` to remove callout text (no page indicator)
   */
  minimal: PropTypes.bool,
  /**
   * Function to run when fast forward button is clicked
   */
  onFastForwardClick: PropTypes.func,
  /**
   * Function to run when next button is clicked
   */
  onNextClick: PropTypes.func.isRequired,
  /**
   * Function to run when previous button is clicked
   */
  onPreviousClick: PropTypes.func.isRequired,
  /**
   * Function to run when rewind button is clicked
   */
  onRewindClick: PropTypes.func,
  /**
   * The current page number being viewed
   */
  pageCurrent: PropTypes.number.isRequired,
  /**
   * To limit the number of pages that will be accessible to the user, set `pageLimit` to the
   * maximum accessible page number. Once the user reaches this page number, they will see the
   * callout text change to an indicator with the `displayRangeStart` value (ex. '251+')
   */
  pageLimit: PropTypes.number,
  /**
   * The number of items per page. If you are not including a `totalPages` prop, you must include an `itemTotal`.
   */
  pageSize: PropTypes.number.isRequired,
  /**
   * The total number of pages (if this information is not provided, it will be calculated based
   * on `itemTotal` and `pageSize`) (Note: if you are not using this prop, you must include `itemTotal`)
   */
  totalPages: PropTypes.number,
};

export default Pagination;
